// Tests for teachers and admins: their batches' tests, building one from the question bank, publishing
// it, the results (each student's best attempt, and how each question went) and marking mock exams.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Link, navigate, useLeaveGuard, useRoute } from '../router.js';
import { rpc } from '../api.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Field, Loading, Notice, Section, Sheet, useRpc, useToast } from '../ui/components.jsx';
import { Rich, firstLine } from '../ui/rich.jsx';
import { useApp } from '../state.js';
import { AnswerReview, QuestionReview, Scenario, TYPE_LABEL, marksLabel, percent, scoreText } from '../ui/question.jsx';
import { dateShort, dateTime, istDate, istTime, plural } from '../format.js';

function statusOf(z) {
  if (!z.published_at) return ['plain', 'Draft'];
  if (z.opens_at && new Date(z.opens_at) > new Date()) return ['gold', `Opens ${dateTime(z.opens_at)}`];
  if (z.closes_at && new Date(z.closes_at) <= new Date()) return ['navy', 'Closed'];
  return ['ok', 'Open'];
}

/* ---------------------------------------------------------------- list */
export function StaffTests() {
  const { query } = useRoute();
  const { data, error, loading, reload } = useRpc('staff_quizzes', {});
  const [creating, setCreating] = useState(query.get('new') === '1');
  return (
    <Shell title="Tests" actions={<Button variant="gold" size="sm" icon="plus" onClick={() => setCreating(true)}>New test</Button>}>
      <div class="tiles tiles-2">
        <Link class="tile" href="/questions"><Icon name="bank" /><span><strong>Question bank</strong><small>Write questions, or upload them from Excel</small></span><Icon name="next" /></Link>
      </div>
      {loading && !data ? <Loading rows={3} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && data.to_mark && data.to_mark.length ? (
        <Section title={`To mark (${data.to_mark.length})`} id="t-to-mark">
          <ul class="list">
            {data.to_mark.map((a) => (
              <li key={a.id}>
                <Link class="list-row mark-row" href={`/tests/attempts/${a.id}`}>
                  <span class="subject">{(a.batch && a.batch.subject) || '•'}</span>
                  <span class="list-text">
                    <strong>{a.student}</strong>
                    <small>{a.quiz.title} · submitted {dateTime(a.submitted_at)}{a.auto_submitted ? ' (time ran out)' : ''}</small>
                  </span>
                  <span class="btn btn-gold btn-sm" aria-hidden="true">Mark</span>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {data && !data.quizzes.length ? (
        <Empty icon="tests" title="No tests yet" action={<Button variant="navy" icon="plus" onClick={() => setCreating(true)}>New test</Button>}>
          Make a practice test or a mock exam for a batch from questions in the bank. Practice tests are marked as students submit; you mark a mock’s written answers.
        </Empty>
      ) : null}
      {data && data.quizzes.length ? (
        <ul class="list test-list">
          {data.quizzes.map((z) => {
            const [tone, label] = statusOf(z);
            return (
              <li key={z.id}>
                <Link class="test-row" href={`/tests/${z.id}`}>
                  <span class="subject">{z.batch.subject || '•'}</span>
                  <span class="test-row-text">
                    <strong>{z.title}{z.kind === 'mock' ? <>{' '}<span class="kind-tag">Mock exam</span></> : null}</strong>
                    <small>{[z.batch.name, z.class && z.class.title].filter(Boolean).join(' · ')}</small>
                    <small>{plural(Number(z.counts.questions), 'question')} · {z.students_done} of {z.students} students done{z.average !== null && z.average !== undefined ? ` · average ${z.average}%` : ''}</small>
                  </span>
                  <span class="test-row-end"><Chip tone={tone}>{label}</Chip>{Number(z.to_mark) ? <span class="to-mark">{z.to_mark} to mark</span> : null}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
      {data ? <TestDetails open={creating} batches={data.batches} preset={{ batch: query.get('batch'), cls: query.get('class') }}
        onClose={() => setCreating(false)} onSaved={(z) => navigate(`/tests/${z.id}`)} /> : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- details (new or edit) */
const toLocal = (iso) => (iso ? { date: istDate(iso), time: istTime(iso) } : { date: '', time: '' });
const fromLocal = (d, t) => (d ? `${d}T${t || '00:00'}:00+05:30` : null);

// The form mounts afresh each time the sheet opens, so it always starts from the test as saved.
function TestDetails(props) {
  return (
    <Sheet title={props.z ? 'Test details' : 'New test'} open={props.open} onClose={props.onClose}>
      <TestDetailsForm {...props} />
    </Sheet>
  );
}

function TestDetailsForm({ z, batches, classes, preset, onClose, onSaved, locked }) {
  const toast = useToast();
  const [f, setF] = useState(() => (z ? {
    kind: z.kind || 'practice',
    batch_id: z.batch.id, class_id: z.class ? z.class.id : '', title: z.title, instructions: z.instructions || '',
    timed: Boolean(z.time_limit_min), time_limit_min: String(z.time_limit_min || 30), attempts: z.attempts_allowed ? String(z.attempts_allowed) : '',
    reveal: z.reveal, shuffle: z.shuffle, opens: toLocal(z.opens_at), closes: toLocal(z.closes_at),
    compulsory: z.compulsory === true,
  } : {
    kind: 'practice',
    batch_id: (preset && preset.batch) || (batches && batches[0] && batches[0].id) || '', class_id: (preset && preset.cls) || '',
    title: '', instructions: '', timed: false, time_limit_min: '30', attempts: '', reveal: 'after_submit', shuffle: false,
    opens: { date: '', time: '' }, closes: { date: '', time: '' }, compulsory: true,
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [batchClasses, setBatchClasses] = useState(classes || []);
  useEffect(() => { // classes of the chosen batch, to tie the test to one
    if (z || !f.batch_id) return undefined;
    let live = true;
    rpc('get_batch', { p_batch_id: f.batch_id }).then((b) => { if (live) setBatchClasses(b.classes.filter((c) => !c.is_loose)); }).catch(() => setBatchClasses([]));
    return () => { live = false; };
  }, [f.batch_id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value });
  const mock = f.kind === 'mock';
  // A mock with no time limit set yet starts with 3 hours, for the teacher to change to the paper's length.
  const setKind = (kind) => setF({ ...f, kind, ...(kind === 'mock' && !f.timed ? { timed: true, time_limit_min: '180' } : {}) });
  // Answers kept back until the test closes: one attempt each (or the first attempt would show them), and a closing time.
  const hidden = !mock && f.reveal === 'after_close';
  const setWhen = (k, part) => (e) => setF({ ...f, [k]: { ...f[k], [part]: e.currentTarget.value } });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      let res = await rpc('staff_save_quiz', { p_quiz: {
        id: z ? z.id : null, batch_id: f.batch_id, class_id: f.class_id || null, title: f.title, kind: f.kind,
        instructions: f.instructions, time_limit_min: f.timed ? Number(f.time_limit_min) : null,
        attempts_allowed: mock || hidden ? 1 : f.attempts ? Number(f.attempts) : null, reveal: mock ? 'after_submit' : f.reveal, shuffle: f.shuffle,
        opens_at: fromLocal(f.opens.date, f.opens.time), closes_at: fromLocal(f.closes.date, f.closes.time || '23:59'),
      } });
      // Compulsory (0011_compulsory_tests.sql): its own call, made only when it changes.
      if (typeof res.compulsory === 'boolean' && res.compulsory !== f.compulsory) {
        res = await rpc('staff_set_quiz_compulsory', { p_quiz_id: res.id, p_compulsory: f.compulsory });
      }
      toast(z ? (mock ? 'Mock saved' : 'Test saved') : `${mock ? 'Mock exam' : 'Test'} created. Now add its questions.`);
      onClose();
      onSaved(res);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  const list = z ? classes : batchClasses;
  return (
      <form class="form" onSubmit={save}>
        {!z ? (
          <Field label="Batch">{(id) => (
            <select id={id} value={f.batch_id} onChange={(e) => setF({ ...f, batch_id: e.currentTarget.value, class_id: '' })} required>
              {batches.map((b) => <option value={b.id} key={b.id}>{b.name}</option>)}
            </select>
          )}</Field>
        ) : null}
        <Field label="Title">{(id) => <input id={id} type="text" required maxLength={200} value={f.title} onInput={set('title')} placeholder={mock ? 'FR mock exam 1' : 'Consolidation: quick check'} />}</Field>
        <div class="field" role="group" aria-labelledby="kind-label">
          <span class="field-label" id="kind-label">Type</span>
          <div class="seg">
            <button type="button" class={`quick-btn${!mock ? ' is-on' : ''}`} aria-pressed={!mock ? 'true' : 'false'} disabled={locked} onClick={() => setKind('practice')}>Practice test</button>
            <button type="button" class={`quick-btn${mock ? ' is-on' : ''}`} aria-pressed={mock ? 'true' : 'false'} disabled={locked} onClick={() => setKind('mock')}>Mock exam</button>
          </div>
          <p class="field-hint">{mock
            ? 'One sitting. Objective questions are marked automatically; you mark the written answers, then release the results.'
            : 'Marked as soon as students submit. Students check written answers themselves against your model answer.'}
            {locked ? ' Students have started it, so the type stays.' : ''}</p>
        </div>
        <Field label="For one class (optional)" hint="It also shows on that class’s page.">{(id) => (
          <select id={id} value={f.class_id} onChange={set('class_id')}>
            <option value="">The whole batch</option>
            {(list || []).map((c) => <option value={c.id} key={c.id}>{c.class_date ? `${dateShort(c.class_date)}: ` : ''}{c.title}</option>)}
          </select>
        )}</Field>
        <Field label="Instructions (optional)">{(id) => <textarea id={id} rows={3} maxLength={5000} value={f.instructions} onInput={set('instructions')} />}</Field>
        <label class="check"><input type="checkbox" checked={f.timed} onChange={set('timed')} /><span>Time limit</span></label>
        {f.timed ? <Field label="Minutes">{(id) => <input id={id} type="number" min={1} max={600} required value={f.time_limit_min} onInput={set('time_limit_min')} />}</Field> : null}
        <div class="row-2">
          <Field label="Opens (optional)" hint="Date, then time (India).">{(id) => (
            <span class="when"><input id={id} type="date" value={f.opens.date} onInput={setWhen('opens', 'date')} />
              <input type="time" aria-label="Opening time" value={f.opens.time} onInput={setWhen('opens', 'time')} /></span>
          )}</Field>
          <Field label={hidden ? 'Closes' : 'Closes (optional)'} hint="Leave the time empty for 11:59 pm.">{(id) => (
            <span class="when"><input id={id} type="date" required={hidden} value={f.closes.date} onInput={setWhen('closes', 'date')} />
              <input type="time" aria-label="Closing time" value={f.closes.time} onInput={setWhen('closes', 'time')} /></span>
          )}</Field>
        </div>
        {!mock ? <div class="row-2">
          <Field label="Show answers" hint={hidden ? 'Needs a closing time and one attempt each.' : null}>{(id) => (
            <select id={id} value={f.reveal} onChange={set('reveal')}>
              <option value="after_submit">As soon as they submit</option>
              <option value="after_close">After the test closes</option>
            </select>
          )}</Field>
          <Field label="Attempts allowed" hint={hidden ? 'One, so no one sees the answers early.' : 'Empty: as many as they like.'}>{(id) => (
            <input id={id} type="number" min={1} max={20} value={hidden ? '1' : f.attempts} disabled={hidden} onInput={set('attempts')} placeholder="No limit" />
          )}</Field>
        </div> : null}
        <label class="check"><input type="checkbox" checked={f.shuffle} onChange={set('shuffle')} /><span>Shuffle the questions for each student (a case’s questions stay together)</span></label>
        <div class="field">
          <label class="check"><input type="checkbox" checked={f.compulsory} onChange={set('compulsory')} aria-describedby="compulsory-hint" /><span>Compulsory</span></label>
          <p class="field-hint" id="compulsory-hint">{f.compulsory && !f.closes.date
            ? 'Set when it closes: students who haven’t done it then get a reminder 3 hours before, and their parents are told if they miss it.'
            : 'Students who haven’t done it get a reminder 3 hours before it closes. Parents who follow a student who misses it get a notification (between 8 am and 9 pm).'}</p>
        </div>
        {err ? <p class="field-error" role="alert">{err}</p> : null}
        <div class="form-actions"><Button type="submit" variant="navy" busy={busy}>{z ? 'Save' : mock ? 'Create mock' : 'Create test'}</Button><Button variant="ghost" onClick={onClose}>Cancel</Button></div>
      </form>
  );
}

/* ---------------------------------------------------------------- one test */
export function StaffTest({ id }) {
  const toast = useToast();
  const { data: z, error, loading, reload, setData } = useRpc('staff_get_quiz', { p_id: id }, [id]);
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copying, setCopying] = useState(false);
  const run = async (what, fn, message) => {
    setBusy(what);
    try { const res = await fn(); if (res && res.id) setData(res); if (message) toast(message); } catch (ex) { toast(ex.message, 'bad'); }
    setBusy('');
  };
  const setItems = (ids, message) => run('items', () => rpc('staff_set_quiz_items', { p_quiz_id: id, p_question_ids: ids }), message);
  const move = (i, d) => {
    const ids = z.items.map((it) => it.question_id);
    const [x] = ids.splice(i, 1);
    ids.splice(i + d, 0, x);
    setItems(ids);
  };
  const remove = (i) => setItems(z.items.map((it) => it.question_id).filter((_, j) => j !== i), 'Removed');
  const del = async () => {
    setBusy('delete');
    try { await rpc('staff_delete_quiz', { p_quiz_id: id }); toast('Test deleted'); navigate('/tests'); } catch (ex) { toast(ex.message, 'bad'); setBusy(''); }
  };
  if (loading && !z) return <Shell backTo="/tests" backLabel="Tests"><Loading rows={4} /></Shell>;
  if (error) return <Shell backTo="/tests" backLabel="Tests"><ErrorBox error={error} onRetry={reload} /></Shell>;
  if (!z) return null;
  const [tone, label] = statusOf(z);
  const missing = z.items.some((it) => !it.q);
  const mock = z.kind === 'mock';
  const written = Number(z.counts.written) || 0;
  return (
    <Shell backTo="/tests" backLabel="Tests" actions={<Link class="btn btn-line btn-sm" href={`/tests/${z.id}/results`}><Icon name="chart" /><span>{mock ? 'Results and marking' : 'Results'}</span></Link>}>
      <p class="eyebrow">{[z.batch.name, z.class && z.class.title].filter(Boolean).join(' · ')}</p>
      <h1 class="page-title">{z.title}{mock ? <>{' '}<span class="kind-tag">Mock exam</span></> : null}</h1>
      <p class="test-status"><Chip tone={tone}>{label}</Chip><span>{plural(Number(z.counts.questions), 'question')} · {marksLabel(z.counts.marks)}
        {written ? (mock ? ` (${written} written, marked by you)` : ` + ${written} written (self-checked)`) : ''}</span></p>
      <dl class="facts">
        <div><dt>Time</dt><dd>{z.time_limit_min ? `${z.time_limit_min} minutes` : 'No time limit'}</dd></div>
        <div><dt>Open</dt><dd>{z.opens_at ? dateTime(z.opens_at) : 'When published'} – {z.closes_at ? dateTime(z.closes_at) : 'no closing time'}</dd></div>
        <div><dt>Answers shown</dt><dd>{mock
          ? `${z.results_released_at ? `Results released ${dateTime(z.results_released_at)}` : 'When you release the results'} · one sitting`
          : `${z.reveal === 'after_close' ? 'After it closes' : 'As soon as they submit'}${z.attempts_allowed ? ` · ${plural(z.attempts_allowed, 'attempt')}` : ' · unlimited attempts'}`}</dd></div>
        {typeof z.compulsory === 'boolean' ? <div><dt>Compulsory</dt><dd>{!z.compulsory ? 'No'
          : !z.closes_at ? 'Yes, once it has a closing time'
            : z.parents_told_at ? `Yes · parents told ${dateTime(z.parents_told_at)}`
              : 'Yes · a reminder 3 hours before it closes; parents told if missed'}</dd></div> : null}
      </dl>
      {mock && Number(z.to_mark) ? (
        <Notice icon="edit"><div><span>{z.to_mark === 1 ? '1 script is waiting to be marked.' : `${z.to_mark} scripts are waiting to be marked.`}</span>
          <Link class="btn btn-gold btn-sm" href={`/tests/${z.id}/results`}><Icon name="edit" /><span>Mark them</span></Link></div></Notice>
      ) : null}
      <div class="row">
        <Button variant="line" icon="edit" onClick={() => setEditing(true)}>Edit details</Button>
        {z.published_at
          ? <Button variant="line" icon="eye" busy={busy === 'pub'} onClick={() => run('pub', () => rpc('staff_publish_quiz', { p_quiz_id: id, p_publish: false }), 'Unpublished: students can’t see it now')}>Unpublish</Button>
          : <Button variant="gold" icon="send" busy={busy === 'pub'} disabled={!z.items.length || missing}
              onClick={() => run('pub', () => rpc('staff_publish_quiz', { p_quiz_id: id, p_publish: true }), 'Published. Students with reminders on get a notification.')}>Publish</Button>}
        <Button variant="ghost" icon="copy" onClick={() => setCopying(true)}>Copy</Button>
        {!z.locked ? <Button variant="ghost" icon="trash" onClick={() => setConfirmDelete(true)}>Delete</Button> : null}
      </div>
      {z.locked ? <Notice icon="info">Students have started this test, so its questions are fixed. You can still change its title, dates and attempts. To change the questions, copy it.</Notice> : null}
      <Section title={`Questions (${z.items.length})`} id="t-items" action={!z.locked ? <Button variant="navy" size="sm" icon="plus" onClick={() => setPicking(true)}>Add questions</Button> : null}>
        {!z.items.length ? <Empty icon="bank" title="No questions yet" action={<Button variant="navy" icon="plus" onClick={() => setPicking(true)}>Add from the bank</Button>}>Pick questions from the bank, or write new ones there first.</Empty> : (
          <ol class="item-list">
            {z.items.map((it, i) => {
              const q = it.q || {};
              return (
                <li class="item-row" key={it.id}>
                  <span class="item-no">{i + 1}</span>
                  <span class="item-text">
                    <strong>{firstLine(q.body) || 'Deleted from the bank'}</strong>
                    <small>{[TYPE_LABEL[q.type], q.topic, q.marks ? marksLabel(q.marks) : null, q.case_title ? `Case: ${q.case_title}` : null].filter(Boolean).join(' · ')}</small>
                    {it.changed ? <small class="item-warn"><Icon name="info" />Changed in the bank since publishing; this test keeps the version students see.</small> : null}
                  </span>
                  {!z.locked ? (
                    <span class="item-actions">
                      <button type="button" class="icon-btn" disabled={i === 0 || Boolean(busy)} onClick={() => move(i, -1)} aria-label={`Move question ${i + 1} up`}><Icon name="up" /></button>
                      <button type="button" class="icon-btn" disabled={i === z.items.length - 1 || Boolean(busy)} onClick={() => move(i, 1)} aria-label={`Move question ${i + 1} down`}><Icon name="down" /></button>
                      <button type="button" class="icon-btn" disabled={Boolean(busy)} onClick={() => remove(i)} aria-label={`Remove question ${i + 1}`}><Icon name="close" /></button>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
      </Section>
      <TestDetails open={editing} z={z} classes={z.classes} locked={z.locked} onClose={() => setEditing(false)} onSaved={() => reload(true)} />
      <Picker open={picking} subject={z.batch.subject} have={z.items.map((it) => it.question_id)} onClose={() => setPicking(false)}
        onAdd={(ids) => setItems([...z.items.map((it) => it.question_id), ...ids], `${plural(ids.length, 'question')} added`)} />
      <Sheet title="Copy this test" open={copying} onClose={() => setCopying(false)}>
        <CopyTestForm z={z} onClose={() => setCopying(false)} />
      </Sheet>
      <Sheet title="Delete this test?" open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <div class="form">
          <p>The questions stay in the bank.</p>
          <div class="form-actions"><Button variant="danger" icon="trash" busy={busy === 'delete'} onClick={del}>Delete test</Button><Button variant="ghost" onClick={() => setConfirmDelete(false)}>Keep it</Button></div>
        </div>
      </Sheet>
    </Shell>
  );
}

// A new draft with the same questions (as they are in the bank now) and settings, for this batch or
// another of the teacher's.
function CopyTestForm({ z, onClose }) {
  const toast = useToast();
  const [batches, setBatches] = useState([z.batch]);
  const [batch, setBatch] = useState(z.batch.id);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    rpc('staff_quizzes', { p_batch_id: z.batch.id }).then((d) => { if (live && d.batches.length) setBatches(d.batches); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const copy = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const res = await rpc('staff_copy_quiz', { p_quiz_id: z.id, p_batch_id: batch });
      const left = Number(res.left_out) || 0;
      toast(left ? `Copied as a draft. ${plural(left, 'question')} archived in the bank ${left === 1 ? 'was' : 'were'} left out.` : 'Copied as a draft. Check its details, then publish.');
      onClose();
      navigate(`/tests/${res.id}`);
    } catch (ex) { setErr(ex.message); setBusy(false); }
  };
  return (
    <form class="form" onSubmit={copy}>
      <p>The copy is a draft with the same settings and questions, taken from the bank as they are now. Change what you need, then publish it.</p>
      <Field label="For the batch">{(id) => (
        <select id={id} value={batch} onChange={(e) => setBatch(e.currentTarget.value)}>
          {batches.map((b) => <option value={b.id} key={b.id}>{b.name}</option>)}
        </select>
      )}</Field>
      {err ? <p class="field-error" role="alert">{err}</p> : null}
      <div class="form-actions"><Button type="submit" variant="navy" icon="copy" busy={busy}>Make the copy</Button><Button variant="ghost" onClick={onClose}>Cancel</Button></div>
    </form>
  );
}

/* ---------------------------------------------------------------- picking questions */
function Picker({ open, subject, have, onClose, onAdd }) {
  const [filter, setFilter] = useState({ subject: subject || '', topic: '', type: '', search: '' });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // Picked units (a question, or a case's questions), kept while the filters and search change.
  const [picked, setPicked] = useState(() => new Map());
  useLayoutEffect(() => { if (open) { setPicked(new Map()); setFilter({ subject: subject || '', topic: '', type: '', search: '' }); } }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    const t = setTimeout(() => {
      rpc('staff_questions', { p_filter: filter }).then((d) => { if (live) { setData(d); setErr(null); } }).catch((e) => live && setErr(e));
    }, filter.search ? 250 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [open, filter.subject, filter.topic, filter.type, filter.search]);
  const already = useMemo(() => new Set(have), [have]);
  // A case is picked as one unit: all its questions, in order.
  const units = useMemo(() => {
    const out = [];
    const seen = new Map();
    for (const q of (data && data.questions) || []) {
      if (q.case) {
        if (!seen.has(q.case.id)) { const u = { key: `case-${q.case.id}`, case: q.case, questions: [] }; seen.set(q.case.id, u); out.push(u); }
        seen.get(q.case.id).questions.push(q);
      } else out.push({ key: q.id, questions: [q] });
    }
    return out;
  }, [data]);
  const toggle = (u) => {
    const next = new Map(picked);
    if (next.has(u.key)) next.delete(u.key); else next.set(u.key, u.questions.map((q) => q.id));
    setPicked(next);
  };
  const ids = [...new Set([...picked.values()].flat())].filter((x) => !already.has(x));
  const set = (k) => (e) => setFilter({ ...filter, [k]: e.currentTarget.value, ...(k === 'subject' ? { topic: '' } : {}) });
  return (
    <Sheet title="Add questions" open={open} onClose={onClose}
      footer={<div class="form-actions"><Button variant="navy" icon="plus" disabled={!ids.length} onClick={() => { onAdd(ids); onClose(); }}>{ids.length ? `Add ${plural(ids.length, 'question')}` : 'Add questions'}</Button><Button variant="ghost" onClick={onClose}>Cancel</Button></div>}>
      <div class="filters-grid">
        <Field label="Subject">{(id) => (
          <select id={id} value={filter.subject} onChange={set('subject')}>
            <option value="">All subjects</option>
            {((data && data.subjects) || []).map((s) => <option value={s} key={s}>{s}</option>)}
          </select>
        )}</Field>
        <Field label="Topic">{(id) => (
          <select id={id} value={filter.topic} onChange={set('topic')}>
            <option value="">All topics</option>
            {((data && data.topics) || []).map((s) => <option value={s} key={s}>{s}</option>)}
          </select>
        )}</Field>
        <Field label="Type">{(id) => (
          <select id={id} value={filter.type} onChange={set('type')}>
            <option value="">All types</option>
            {Object.entries(TYPE_LABEL).map(([k, v]) => <option value={k} key={k}>{v}</option>)}
          </select>
        )}</Field>
        <Field label="Search">{(id) => <input id={id} type="search" value={filter.search} onInput={set('search')} placeholder="Words in the question" />}</Field>
      </div>
      {err ? <ErrorBox error={err} /> : null}
      {!data ? <Loading rows={3} /> : !units.length ? <p class="muted">No questions match. Add some in the Question bank.</p> : (
        <ul class="pick-list">
          {units.map((u) => {
            const inTest = u.questions.every((q) => already.has(q.id));
            const q = u.questions[0];
            return (
              <li key={u.key}>
                <label class={`pick-row${inTest ? ' is-in' : ''}`}>
                  <input type="checkbox" checked={inTest || picked.has(u.key)} disabled={inTest} onChange={() => toggle(u)} />
                  <span class="pick-text">
                    <strong>{u.case ? `Case: ${u.case.title}` : firstLine(q.body)}</strong>
                    <small>{u.case ? `${plural(u.questions.length, 'question')} · ${u.questions.map((x) => TYPE_LABEL[x.type]).join(', ')}`
                      : [TYPE_LABEL[q.type], q.subject, q.topic, marksLabel(q.marks), q.difficulty].filter(Boolean).join(' · ')}{inTest ? ' · in this test' : ''}</small>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </Sheet>
  );
}

/* ---------------------------------------------------------------- results */
function studentState(s, mock) {
  if (s.in_progress) return ['gold', 'Taking it'];
  if (!s.attempts) return ['plain', 'Not started'];
  if (mock && s.to_mark) return ['bad', 'To mark'];
  return ['ok', mock ? 'Marked' : 'Done'];
}

function ReleaseCard({ q, onChange }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const released = Boolean(q.results_released_at);
  const done = Number(q.students_done) || 0;
  const marked = Number(q.marked) || 0;
  const sitting = Number(q.sitting) || 0;
  const notSat = Math.max(0, (Number(q.students) || 0) - done - sitting);
  const flip = async (release) => {
    setBusy(true);
    try {
      onChange(await rpc('staff_release_results', { p_quiz_id: q.id, p_release: release }));
      toast(release ? 'Results released. Students get a notification for each marked script.' : 'Results hidden from students again');
    } catch (ex) { toast(ex.message, 'bad'); }
    setBusy(false);
  };
  return (
    <div class={`release-card${released ? ' is-released' : ''}`}>
      <div class="release-text">
        <strong>{released ? 'Results released' : 'Results not released yet'}</strong>
        <span>{released
          ? `Since ${dateTime(q.results_released_at)}. Students see each script with its marks, your comments and the answers as soon as it is marked. The mock is closed to anyone who hasn’t sat it; hide the results to let someone sit it late.`
          : `Students can’t see any marks yet. Marked: ${marked} of ${done} submitted${done && marked < done ? '. You can release now and finish the rest later: each script shows once it is marked.' : '.'}`}</span>
        {!released && sitting ? <span class="release-warn">{sitting === 1 ? '1 student is' : `${sitting} students are`} still sitting it. Release once they have submitted.</span> : null}
        {!released && !sitting && notSat ? <span class="release-warn">{notSat === 1 ? '1 student hasn’t' : `${notSat} students haven’t`} sat it. Releasing shows the answers, so it closes the mock for them.</span> : null}
      </div>
      {released
        ? <Button variant="line" icon="eye" busy={busy} onClick={() => flip(false)}>Hide results</Button>
        : <Button variant="gold" icon="send" busy={busy} disabled={!q.published_at || sitting > 0} onClick={() => flip(true)}>Release results</Button>}
    </div>
  );
}

export function TestResults({ id }) {
  const { data, error, loading, reload, setData } = useRpc('staff_quiz_results', { p_quiz_id: id }, [id]);
  const mock = data && data.quiz.kind === 'mock';
  return (
    <Shell backTo={`/tests/${id}`} backLabel="Test">
      {loading && !data ? <Loading rows={4} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <p class="eyebrow">{data.quiz.batch.name}{mock ? ' · Mock exam' : ''}</p>
          <h1 class="page-title">{data.quiz.title}: results</h1>
          <div class="stats stats-3">
            <div class="stat"><strong>{data.quiz.students_done} / {data.quiz.students}</strong><span>Students done</span></div>
            {mock ? <div class="stat"><strong>{data.quiz.marked} / {data.quiz.students_done}</strong><span>Marked</span></div> : null}
            <div class="stat"><strong>{data.quiz.average === null || data.quiz.average === undefined ? '–' : `${data.quiz.average}%`}</strong><span>{mock ? 'Average (marked)' : 'Average best score'}</span></div>
            {!mock ? <div class="stat"><strong>{data.quiz.attempts}</strong><span>Attempts</span></div> : null}
          </div>
          {mock ? <ReleaseCard q={data.quiz} onChange={(q) => setData({ ...data, quiz: { ...data.quiz, ...q } })} /> : null}
          <Section title="Students" id="r-students">
            <div class="table-wrap" tabIndex={0} role="region" aria-labelledby="r-students">
              <table class="data-table">
                <thead><tr><th scope="col">Student</th><th scope="col" class="num">{mock ? 'Marks' : 'Best'}</th>
                  {mock ? <th scope="col">Status</th> : <th scope="col" class="num">Attempts</th>}
                  <th scope="col">{mock ? 'Submitted' : 'Last'}</th><th scope="col"><span class="sr-only">Open</span></th></tr></thead>
                <tbody>
                  {data.students.map((s) => {
                    const [tone, label] = studentState(s, mock);
                    const scored = s.best !== null && s.best !== undefined;
                    return (
                      <tr key={s.id}>
                        <th scope="row">{s.name}</th>
                        <td class="num">{scored ? <>{scoreText(s.best, s.best_of)} <small>{percent(s.best, s.best_of)}%</small></> : !mock && s.in_progress ? 'Taking it' : '–'}</td>
                        {mock ? <td><Chip tone={tone}>{label}</Chip></td> : <td class="num">{s.attempts}</td>}
                        <td>{s.last_at ? dateTime(s.last_at) : ''}</td>
                        <td>{mock && s.to_mark
                          ? <Link class="btn btn-gold btn-sm" href={`/tests/attempts/${s.to_mark}`} aria-label={`Mark ${s.name}'s script`}>Mark</Link>
                          : s.best_attempt ? <Link href={`/tests/attempts/${s.best_attempt}`} aria-label={mock ? `${s.name}'s script` : `${s.name}'s best attempt`}>View</Link> : null}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
          <Section title="Questions" id="r-items">
            <p class="muted">{mock
              ? 'How many students got each objective question right, and the average mark for each written one (marked scripts). Low ones are worth going over in class.'
              : 'How many students got each question right in their submitted attempts. Low ones are worth going over in class.'}</p>
            <ol class="item-stats">
              {data.items.map((it, i) => {
                const writtenMarked = mock && it.q.type === 'written';
                const pct = writtenMarked
                  ? (it.average !== null && it.average !== undefined ? Math.round((Number(it.average) * 100) / Number(it.q.marks)) : null)
                  : it.answered && it.q.type !== 'written' && it.submitted ? Math.round((it.correct * 100) / it.submitted) : null;
                return (
                  <li key={it.id} class={pct !== null && pct < 50 ? 'is-low' : ''}>
                    <span class="item-no">{i + 1}</span>
                    <span class="item-text"><strong>{firstLine(it.q.body)}</strong><small>{[TYPE_LABEL[it.q.type], it.q.topic, writtenMarked ? marksLabel(it.q.marks) : null].filter(Boolean).join(' · ')}</small></span>
                    {it.q.type === 'written' && !mock ? <span class="item-pct muted">Self-checked</span> : (
                      <span class="item-pct">
                        <span class="bar" role="img" aria-label={pct === null ? 'No marks yet' : writtenMarked ? `Average ${Number(it.average)} of ${Number(it.q.marks)}` : `${pct}% right`}><span style={{ width: `${pct || 0}%` }} /></span>
                        <span>{pct === null ? '–' : writtenMarked ? `${Number(it.average)} / ${Number(it.q.marks)}` : `${pct}%`}</span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </Section>
        </>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- marking a mock script */
const half = (v) => Number.isFinite(v) && Math.round(v * 2) === v * 2;

export function MarkScript({ a, onSaved }) {
  const toast = useToast();
  const { reloadHome } = useApp();
  const writtenItems = a.items.filter((it) => it.q.type === 'written');
  const start = () => Object.fromEntries(writtenItems.map((it) => [it.id, {
    score: it.score === null || it.score === undefined ? '' : String(Number(it.score)), comment: it.comment || '',
  }]));
  const [marks, setMarks] = useState(start);
  const [feedback, setFeedback] = useState(a.feedback || '');
  const [busy, setBusy] = useState('');
  const [errors, setErrors] = useState({});
  const saved = useRef(JSON.stringify([start(), a.feedback || '']));
  useLayoutEffect(() => { // a different script: start again from what is saved
    const s0 = start();
    setMarks(s0); setFeedback(a.feedback || ''); setErrors({});
    saved.current = JSON.stringify([s0, a.feedback || '']);
  }, [a.id]);
  const dirty = JSON.stringify([marks, feedback]) !== saved.current;
  useLeaveGuard(dirty, 'Leave this script without saving your marks?');

  const objective = a.items.filter((it) => it.q.type !== 'written');
  const objScore = objective.reduce((t, it) => t + (Number(it.score) || 0), 0);
  const objMax = objective.reduce((t, it) => t + Number(it.q.marks), 0);
  const writtenMax = writtenItems.reduce((t, it) => t + Number(it.q.marks), 0);
  const writtenGiven = writtenItems.reduce((t, it) => t + (marks[it.id].score === '' ? 0 : Number(marks[it.id].score) || 0), 0);
  const left = writtenItems.filter((it) => marks[it.id].score === '').length;
  const marked = Boolean(a.marked_at);

  const setMark = (itemId, patch) => {
    setMarks({ ...marks, [itemId]: { ...marks[itemId], ...patch } });
    if (errors[itemId]) setErrors({ ...errors, [itemId]: null });
  };
  const check = () => {
    const found = {};
    for (const it of writtenItems) {
      const v = marks[it.id].score;
      if (v === '') continue;
      const n = Number(v);
      if (!half(n) || n < 0 || n > Number(it.q.marks)) found[it.id] = `Marks from 0 to ${Number(it.q.marks)}, in halves.`;
    }
    setErrors(found);
    return !Object.keys(found).length;
  };
  const save = async (finish) => {
    if (!check()) { toast('Check the marks shown in red.', 'bad'); return; }
    setBusy(finish ? 'finish' : 'save');
    try {
      // Only what changed since the last save, so two people marking never undo each other's work.
      const [before, feedbackBefore] = JSON.parse(saved.current);
      const payload = {};
      for (const it of writtenItems) {
        const now = marks[it.id];
        const was = before[it.id] || {};
        const patch = {};
        if (now.score !== was.score) patch.score = now.score === '' ? null : Number(now.score);
        if (now.comment !== was.comment) patch.comment = now.comment;
        if (Object.keys(patch).length) payload[it.id] = patch;
      }
      const res = await rpc('staff_mark_attempt', { p_attempt_id: a.id, p_marks: payload,
        p_feedback: feedback !== feedbackBefore ? feedback : null, p_finish: finish });
      saved.current = JSON.stringify([marks, feedback]);
      reloadHome(true); // the "to mark" badge
      if (finish && !marked) {
        toast(res.next ? `Marked: ${scoreText(res.score, res.max_score)}. On to the next script.` : `Marked: ${scoreText(res.score, res.max_score)}. That was the last one.`);
        navigate(res.next ? `/tests/attempts/${res.next}` : `/tests/${a.quiz.id}/results`, { force: true });
      } else {
        toast(marked ? 'Marks saved' : 'Saved. Finish marking when you’re ready.');
        onSaved(res);
      }
    } catch (ex) { toast(ex.message, 'bad'); }
    setBusy('');
  };

  return (
    <Shell backTo={`/tests/${a.quiz.id}/results`} backLabel="Results">
      <article class="review marking">
        <p class="eyebrow">{a.quiz.batch ? a.quiz.batch.name : ''} · Mock exam</p>
        <h1 class="page-title">{a.student ? a.student.name : 'Script'}</h1>
        <p class="test-status">
          <Chip tone={marked ? 'ok' : 'bad'}>{marked ? 'Marked' : 'To mark'}</Chip>
          <span>{a.quiz.title} · submitted {dateTime(a.submitted_at)}{a.auto_submitted ? ' (time ran out)' : ''}{marked && a.marked_by ? ` · marked by ${a.marked_by}` : ''}</span>
        </p>
        <div class="stats stats-3 mark-stats">
          <div class="stat"><strong>{scoreText(objScore, objMax)}</strong><span>Objective (marked automatically)</span></div>
          <div class="stat"><strong>{scoreText(writtenGiven, writtenMax)}</strong><span>Written{left ? ` (${left} to mark)` : ''}</span></div>
          <div class="stat"><strong>{scoreText(objScore + writtenGiven, objMax + writtenMax)}</strong><span>Total so far · {percent(objScore + writtenGiven, objMax + writtenMax)}%</span></div>
        </div>
        {marked && a.quiz.released ? <Notice icon="info">The results are released, so the student sees changes as soon as you save them.</Notice> : null}
        <div class="review-list">
          {a.items.map((it) => (it.q.type !== 'written' ? (
            <QuestionReview item={it} revealed number={it.number} key={it.id} />
          ) : (
            <article class="review-q mark-q" aria-labelledby={`mq-${it.id}`} key={it.id}>
              <header class="review-q-head">
                <h3 id={`mq-${it.id}`}>Question {it.number}</h3>
                <span class="review-q-meta"><Chip tone={marks[it.id].score === '' ? 'bad' : 'navy'}>{marks[it.id].score === '' ? 'To mark' : `${marks[it.id].score} / ${marksLabel(it.q.marks)}`}</Chip></span>
              </header>
              <Scenario q={it.q} open={false} />
              <Rich text={it.q.body} class="q-body" />
              <AnswerReview item={it} revealed whose="The student’s answer" />
              {it.q.explanation ? (
                <details class="guide">
                  <summary>Model answer and marking guide</summary>
                  <Rich text={it.q.explanation} />
                </details>
              ) : null}
              <div class="mark-fields">
                <Field label={`Marks (out of ${Number(it.q.marks)})`} error={errors[it.id]}>{(fid) => (
                  <input id={fid} type="number" inputMode="decimal" min={0} max={Number(it.q.marks)} step={0.5} value={marks[it.id].score}
                    onInput={(e) => setMark(it.id, { score: e.currentTarget.value })} />
                )}</Field>
                <Field label="Comment for the student (optional)">{(fid) => (
                  <textarea id={fid} rows={3} maxLength={5000} value={marks[it.id].comment} onInput={(e) => setMark(it.id, { comment: e.currentTarget.value })} />
                )}</Field>
              </div>
            </article>
          )))}
        </div>
        <Section title="Overall feedback" id="m-feedback">
          <Field label="Feedback for the student (optional)" hint="Shown above their marks when the results are released.">{(fid) => (
            <textarea id={fid} rows={4} maxLength={5000} value={feedback} onInput={(e) => setFeedback(e.currentTarget.value)} />
          )}</Field>
        </Section>
        <div class="form-actions mark-actions">
          {marked
            ? <Button variant="navy" icon="check" busy={busy === 'finish'} disabled={!dirty} onClick={() => save(true)}>Save changes</Button>
            : <>
                <Button variant="gold" icon="check" busy={busy === 'finish'} onClick={() => save(true)}>Finish marking</Button>
                <Button variant="line" busy={busy === 'save'} disabled={!dirty} onClick={() => save(false)}>Save for later</Button>
              </>}
        </div>
      </article>
    </Shell>
  );
}
